import React, { FunctionComponent, useEffect } from 'react';
import { ISchemaFieldProps } from 'src/enquiryForm/model/IJsonSchema';
import snarkdown from 'snarkdown';
import markdownTextStyles from './markdownTextStyles';
import { Typography } from '@material-ui/core';
import useCheckoutContext from '../.../../../../../../gql/common/hooks/useCheckoutContext';
import usePlatformConfig from '../.../../../../../../common/react/usePlatformConfig';
import useJourneyStepsContext from '../.../../../../../../gql/checkout4/hooks/useJourneyStepsContext';
import useNewDxpDigitalAssignmentEntryEvent from '../.../../../../../../gql/common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import {
  computeMonthly,
  getBookingDetailsCommonFields,
  getCheckoutStepsCommonFields,
  getEventDataCommonFields,
  getPurchaseOptionCommonFields,
  getRetailerCommonFields,
  getTradeInCommonFields,
  vproductCommonFields,
  removeHostNameFromUrl,
} from '../../../../../gql/common/utils/dxpDataLayerCommonFields';
import { DxpDataLayerEventNames } from '../../../../../../../common/utilities/customDatalayerInterface';
import { EventDataTypes } from '../../../../../gql/common/enum/DataLayerEnums';

const MarkdownText: FunctionComponent<ISchemaFieldProps> = ({ formData }) => {
  const styles = markdownTextStyles();
  const {
    platformConfig: { ga4DxpDataLayer },
  } = usePlatformConfig();
  const { deal } = useCheckoutContext();
  const { steps, activeStep } = useJourneyStepsContext();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();
  const { label, formContext } = formData;

  useEffect(() => {
    const { tradeIn } = formContext;
    if (jlrDataLayer) {
      const links = Array.prototype.slice.call(
        document.querySelector('[data-test="text:enquiryForm:markdownText"]')?.getElementsByTagName('a'),
      );
      links.forEach((link: HTMLAnchorElement) => {
        link.addEventListener('click', () => {
          if (deal && link.href && link.textContent) {
            const { attributes, finance, car } = deal;
            const { amount, currency } = car?.prices.totalPrice?.[0]?.money || {};
            const { term, mileage } = finance?.formInput || {};
            const { reserveFee } = attributes.paymentFee || {};

            callDxpDigitalAssignmentEntryEvent(
              {
                ...getEventDataCommonFields(DxpDataLayerEventNames.TNC_PRIVACY_CTA_CLICK, true),
                event_action: removeHostNameFromUrl(link.href),
                event_category: EventDataTypes.CHECKOUT_CLICK,
                event_pagecategory: EventDataTypes.CHECKOUT,
                click_url: removeHostNameFromUrl(link.href),
                click_name: EventDataTypes.ANCHOR_TEXT,
                event_label: `${EventDataTypes.ANCHOR_TEXT}::${link.textContent ? link.textContent.toLowerCase() : ''}`,
                click_text: link.textContent?.toLowerCase() ?? '',
                vproduct: {
                  ...vproductCommonFields(deal),
                  total_weight: '',
                  max_speed: '',
                  est_delivery_time: deal.car?.deliveryTimeFrame?.toString() ?? '',
                },
                checkoutStep: {
                  ...getCheckoutStepsCommonFields(steps, activeStep),
                },
                purchaseOption: {
                  ...getPurchaseOptionCommonFields(deal),
                  vehicle_cash_price: car?.prices?.totalPrice?.[0]?.money ? `${currency} ${amount}`.toLowerCase() : '',
                  terms: term ? `${term} months` : '',
                  monthly_mileage: computeMonthly(mileage, 'km') ?? '',
                  reservation_fee: reserveFee?.[0]?.amount.toString() ?? '',
                  payment_period: deal.finance?.simulation?.loanTermPeriod
                    ? `${deal.finance.simulation.loanTermPeriod.value} ${deal.finance.simulation.loanTermPeriod.unit}`
                    : '',
                  installment_fee: '',
                  down_payment_trade_in_price: '',
                  finance_product: deal.finance?.simulation?.financeProductName
                    ? deal.finance.simulation.financeProductName.toLowerCase()
                    : '',
                  final_deferred_payment: '',
                  total_to_pay: deal.car.prices?.totalPrice?.[1]?.money?.amount.toString() ?? '',
                },
                tradein: {
                  ...getTradeInCommonFields(deal, tradeIn.tradeInCheckbox ? tradeIn : undefined),
                },
                retailer: {
                  ...getRetailerCommonFields(deal),
                },
                bookingDetails: {
                  ...getBookingDetailsCommonFields(deal, formContext),
                },
              },
              jlrDataLayer,
            );
          }
        });
      });
    }
  });

  return (
    <Typography
      className={styles.text}
      dangerouslySetInnerHTML={{ __html: snarkdown(label) }}
      data-test="text:enquiryForm:markdownText"
    />
  );
};

export default MarkdownText;
